import Vue from 'vue';
import App from './App.vue';

import Vuesax from 'vuesax';
import 'material-icons/iconfont/material-icons.css'; //Material Icons
import 'vuesax/dist/vuesax.css'; // Vuesax
import VueMapbox from 'vue-mapbox';
import Mapbox from 'mapbox-gl';
import * as Sentry from '@sentry/vue';

// Signed links (e.g. S4 order approval) carry a bearer token in ?token=.
// Redact it from anything Sentry records.
const TOKEN_PARAM = /([?&]token=)[^&#]*/gi;
const redactUrl = (url) => (typeof url === 'string' ? url.replace(TOKEN_PARAM, '$1[Filtered]') : url);
const redactQuery = (query) => {
  if (typeof query === 'string') return query.replace(/(^|&)(token=)[^&]*/gi, '$1$2[Filtered]');
  if (Array.isArray(query)) {
    return query.map(pair => (Array.isArray(pair) && pair[0] === 'token' ? [pair[0], '[Filtered]'] : pair));
  }
  if (query && typeof query === 'object' && 'token' in query) return { ...query, token: '[Filtered]' };
  return query;
};

Sentry.init({
  App,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  sendDefaultPii: true,
  beforeBreadcrumb(breadcrumb) {
    const { data } = breadcrumb;
    if (data && ['xhr', 'fetch', 'navigation'].includes(breadcrumb.category)) {
      ['url', 'from', 'to'].forEach((key) => {
        if (key in data) data[key] = redactUrl(data[key]);
      });
    }
    return breadcrumb;
  },
  beforeSend(event) {
    const { request } = event;
    if (request) {
      request.url = redactUrl(request.url);
      request.query_string = redactQuery(request.query_string);
      if (request.headers && request.headers.Referer) {
        request.headers.Referer = redactUrl(request.headers.Referer);
      }
    }
    return event;
  },
});

Vue.config.errorHandler = async function (err) {
  Sentry.captureException(err);
  await Sentry.flush(1000);
};

Vue.use(VueMapbox, { mapboxgl: Mapbox });
Vue.use(Vuesax);
    
// Global event bus
export const EventBus = new Vue();

// axios
import axios from './axios.js';
Vue.prototype.$http = axios;

// API Calls
import './http/requests';

// mock
import './fake-db/index.js';

// Theme Configurations
import '../themeConfig.js';

// Firebase
import '@/firebase/firebaseConfig';

// Region Helper
import regionHelper from './globalHelpers/regionHelper';
Vue.use(regionHelper);

// Store Helper
import storeHelper from './globalHelpers/storeHelper';
Vue.use(storeHelper);

// ACL
import acl from './acl/acl';

// Globally Registered Components
import './globalComponents.js';

import './assets/scss/main.scss';

// Tailwind
import '@/assets/css/main.css';
import '@/assets/css/shop-page-scroll.css';

// Vue Router
import router from './router';
import { ensureMigrationChecked } from '@/utils/checkOrgMigration';
import { captureV2PlatformSource } from '@/utils/platformMigrationHelper';

// Vuex Store
import store from './store/store';

// i18n
import i18n from './i18n/i18n';

// Vuexy Admin Filters
import './filters/filters';

// Clipboard
import VueClipboard from 'vue-clipboard2';
Vue.use(VueClipboard);

// Tour
import VueTour from 'vue-tour';
Vue.use(VueTour);
require('vue-tour/dist/vue-tour.css');

// VeeValidate
import VeeValidate from 'vee-validate';
VeeValidate.setMode('eager');

const MIN_PASSWORD_LENGTH = 10;

VeeValidate.Validator.extend('strongPassword', {
  validate: (value) => {
    const regex = new RegExp(`^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])[a-zA-Z0-9_!@#$%^&*()+=-{};:'<>,./?]{${MIN_PASSWORD_LENGTH},}$`);
    return regex.test(value);
  },
  getMessage: () => {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters long and contain at least one uppercase letter, one lowercase letter, one digit and one special character.`;
  },
});

Vue.use(VeeValidate, {
  classes: true,
});

import ElementUI from 'element-ui';
import 'element-ui/lib/theme-chalk/index.css';
import locale from 'element-ui/lib/locale/lang/en';

Vue.use(ElementUI, { locale });

// Vuejs - Vue wrapper for hammerjs
import { VueHammer } from 'vue2-hammer';
Vue.use(VueHammer);

// PrismJS
import 'prismjs';
import 'prismjs/themes/prism-tomorrow.css';

// Feather font icon
require('./assets/css/iconfont.css');

// Inter font
require('./assets/css/interfont.css');

import Paginate from 'vuejs-paginate';
Vue.component('paginate', Paginate);

// Vue select css


Vue.config.productionTip = false;
/** Date Processing */
import moment from 'moment';
Vue.filter('moment', function(date) {
  return moment(date).format('Do MMMM YYYY');
});

/** Currency formatter */
import Vue2Filters from 'vue2-filters';
Vue.use(Vue2Filters);

Vue.mixin({
  methods: {
    open_in_new_tab: function(url) {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('target', '_blank');
      link.click();
    },
    check_has_permission: function(task) {
      const userRole = store.state.AppActiveUser.userType;

      if (
        userRole.toLowerCase() === 'nurse' ||
        userRole.toLowerCase() === 'doctor'
      ) {
        return false;
      }

      if (userRole === 'admin') {
        const permissions = store.state.AppActiveUser.permissions;
        console.log('This is the permission :::', permissions[task]);

        if (
          task === 'viewClinicTreatmentHistory' ||
          task === 'viewClinicTreatmentDetail' ||
          task === 'viewDoctorTreatmentDetail' ||
          task === 'viewDoctorTreatmentHistory' ||
          task === 'viewNurseTreatmentDetail' ||
          task === 'viewNurseTreatmentHistory' ||
          task === 'viewPatientMedicalDetail' ||
          task === 'viewPatientTreatmentHistory' ||
          task === 'viewPatientTreatmentDetail'
        ) {
          if (store.state.AppActiveUser.isMedicalPerson && permissions[task])
            return true;
          else return false;
        } else {
          return permissions[task];
        }
      }

      return true;
    },
  },
});

import './plugins/launchdarkly';
import LDService from './services/launchdarkly';
import jwt from '@/http/requests/auth/jwt/index.js';

function initLaunchDarkly() {
  const { id: key, email } = store.state.AppActiveUser;

  if (key) {
    // Typed context (SDK v3). A typed context needs a string key, or every flag
    // silently returns its fallback. userRole is deliberately not sent: the v2
    // legacy user format ignored unknown top-level fields, so it was never a
    // targeting attribute, and adding it now could start matching dormant
    // userRole rules. Add it on purpose, with a flag audit, if needed.
    // email is kept in case a flag targets on it, but marked private so it stays
    // out of LD analytics events and the Contexts dashboard.
    LDService.init(process.env.VUE_APP_LDCLIENT_KEY, {
      kind: 'user',
      key: String(key),
      email,
      _meta: { privateAttributes: ['email'] },
    });
  }
}
initLaunchDarkly();

import mixpanelService from '@/services/mixpanel';
import FEATURE_FLAGS from '@/constants/featureFlags';
async function initMixpanel() {
  try {  
    const { id: key, firstName, lastName, email, userRole, userType } = store.state.AppActiveUser;
    if(Vue.prototype.$ld) {
      const isMixpanelEnabled = await Vue.prototype.$ld.getFlag(FEATURE_FLAGS.WEB_SESSION_REPLAY_HEATMAP, false);
      if (key && isMixpanelEnabled) {
        mixpanelService.init(process.env.VUE_APP_MIXPANEL_TOKEN);
        mixpanelService.identify(key);
        mixpanelService.setUserProperties({
          $email: email,
          $first_name: firstName,
          $last_name: lastName,
          $user_type: userType,
          $user_role: userRole,
        });
      }
    }
  } catch (error) {
    console.error('Failed to parse user data for Mixpanel identify:', error);
  }
}
initMixpanel();
Vue.prototype.$mixpanel = mixpanelService.getInstance();

jwt.init();

async function bootstrap() {
  captureV2PlatformSource();
  const isLoggedIn = localStorage.getItem(
    `${process.env.VUE_APP_ACCESS_TOKEN_NAME}LoggedIn`
  );
  if (isLoggedIn) {
    const redirected = await ensureMigrationChecked({ trigger: 'app_load', skipped: true });
    if (redirected) {
      return;
    }
  }

  new Vue({
    router,
    store,
    i18n,
    acl,
    render: h => h(App),
  }).$mount('#app');
}

bootstrap();
